import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PurchasedPackagesSection } from "./components/PurchasedPackagesSection";
import { AvailablePackagesSection } from "./components/AvailablePackagesSection";
import {
  getPurchasedPackages,
  getAvailablePackages,
} from "../../../services/student/packages.service";
import {
  PurchasedPackage,
  AvailablePackage,
} from "../../../services/student/packages.interfaces";
import { useAppToast } from "@/hooks/useAppToast";

const PackagesPageSkeleton = () => (
  <div className="px-6 py-10 space-y-8" aria-busy="true" aria-live="polite">
    <p className="sr-only">Carregando pacotes</p>
    <div className="h-8 w-48 rounded-md bg-surface-subtle animate-pulse" />
    <div className="flex gap-4 overflow-hidden">
      <div className="h-40 w-64 shrink-0 rounded-2xl bg-surface-subtle animate-pulse" />
      <div className="h-40 w-64 shrink-0 rounded-2xl bg-surface-subtle animate-pulse" />
    </div>
    <div className="h-6 w-40 rounded-md bg-surface-subtle animate-pulse" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="h-[260px] rounded-2xl bg-surface-subtle animate-pulse" />
      <div className="h-[260px] rounded-2xl bg-surface-subtle animate-pulse" />
    </div>
  </div>
);

type AlreadyOwnedLocationState = {
  name?: string;
};

export const StudentPackagesPage = () => {
  const [purchasedPackages, setPurchasedPackages] = useState<PurchasedPackage[]>(
    []
  );
  const [availablePackages, setAvailablePackages] = useState<AvailablePackage[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [availableError, setAvailableError] = useState(false);
  const [purchasedError, setPurchasedError] = useState(false);
  const [highlightPackageId, setHighlightPackageId] = useState<string | null>(
    null
  );
  const alreadyOwnedHandledRef = useRef<string | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccessToast } = useAppToast();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setAvailableError(false);
      setPurchasedError(false);

      const [purchasedResult, availableResult] = await Promise.allSettled([
        getPurchasedPackages(),
        getAvailablePackages(),
      ]);

      if (purchasedResult.status === "fulfilled") {
        setPurchasedPackages(purchasedResult.value);
      } else {
        console.error("Error fetching purchased packages:", purchasedResult.reason);
        setPurchasedError(true);
        setPurchasedPackages([]);
      }

      if (availableResult.status === "fulfilled") {
        setAvailablePackages(availableResult.value);
      } else {
        console.error("Error fetching available packages:", availableResult.reason);
        setAvailableError(true);
        setAvailablePackages([]);
      }

      setLoading(false);
    };

    void fetchData();
  }, []);

  const buyablePackages = useMemo(() => {
    if (purchasedError) return availablePackages;
    const ownedIds = new Set(purchasedPackages.map((pkg) => pkg.id));
    return availablePackages.filter((pkg) => !ownedIds.has(pkg.id));
  }, [availablePackages, purchasedPackages, purchasedError]);

  const emptyReason =
    availablePackages.length === 0 ? "shop-empty" : "already-owned";

  useEffect(() => {
    if (loading) return;

    const alreadyOwnedId = searchParams.get("alreadyOwned");
    if (!alreadyOwnedId) return;
    if (alreadyOwnedHandledRef.current === alreadyOwnedId) return;

    alreadyOwnedHandledRef.current = alreadyOwnedId;

    const locationState = (location.state as AlreadyOwnedLocationState | null) ?? null;
    const nameFromState = locationState?.name?.trim();
    const nameFromPurchased = purchasedPackages.find(
      (pkg) => pkg.id === alreadyOwnedId
    )?.name;
    const displayName = nameFromState || nameFromPurchased;

    showSuccessToast(
      displayName
        ? `Você já tem ${displayName}.`
        : "Você já tem este pacote."
    );

    if (!purchasedError) {
      const exists = purchasedPackages.some((pkg) => pkg.id === alreadyOwnedId);
      if (exists) {
        setHighlightPackageId(alreadyOwnedId);
        window.setTimeout(() => setHighlightPackageId(null), 3000);
      }
    }

    navigate("/student/packages", { replace: true, state: {} });
  }, [
    loading,
    searchParams,
    location.state,
    purchasedPackages,
    purchasedError,
    navigate,
    showSuccessToast,
  ]);

  const scrollToMyPackages = () => {
    document
      .getElementById("meus-pacotes")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return <PackagesPageSkeleton />;
  }

  if (availableError) {
    return (
      <div className="px-6 py-10">
        <p className="text-sm font-medium text-foreground">
          Não carregamos seus pacotes
        </p>
        <p className="mt-1 text-sm text-text-secondary">
          Atualize a página. Se o erro continuar, tente de novo em alguns minutos.
        </p>
      </div>
    );
  }

  return (
    <>
      <PurchasedPackagesSection
        packages={purchasedPackages}
        highlightPackageId={highlightPackageId}
      />
      <AvailablePackagesSection
        packages={buyablePackages}
        emptyReason={emptyReason}
        onGoToMyPackages={
          emptyReason === "already-owned" ? scrollToMyPackages : undefined
        }
      />
    </>
  );
};
